// src/utils/subscriptionUtils.js

/**
 * Determines the plan a user currently has access to.
 *
 * Rules:
 *  - If a downgrade is scheduled AND the billing period has not yet ended,
 *    the user keeps access to their original (higher) plan → return previousPlanId.
 *  - In all other cases, return planId.
 *
 * @param {object|null} subscription  The currentSubscription object from Redux auth state.
 * @returns {string|null}  e.g. "free" | "pro" | "premium" | null
 */
export function getEffectivePlan(subscription) {
  if (!subscription) return null;

  const { planId, previousPlanId, downgradeScheduled, currentPeriodEnd } =
    subscription;

  if (
    downgradeScheduled &&
    previousPlanId &&
    currentPeriodEnd &&
    new Date(currentPeriodEnd) > new Date()
  ) {
    return previousPlanId;
  }

  return planId ?? null;
}

/**
 * Returns true when a downgrade has been scheduled and the period has not yet ended.
 *
 * @param {object|null} subscription
 * @returns {boolean}
 */
export function isDowngradePending(subscription) {
  if (!subscription) return false;

  const { downgradeScheduled, previousPlanId, currentPeriodEnd } = subscription;

  return Boolean(
    downgradeScheduled &&
    previousPlanId &&
    currentPeriodEnd &&
    new Date(currentPeriodEnd) > new Date(),
  );
}

/**
 * Returns the plan the user will move to once the billing period ends.
 * Returns null if no downgrade is pending.
 *
 * @param {object|null} subscription
 * @returns {string|null}
 */
export function getDowngradeTargetPlan(subscription) {
  if (!isDowngradePending(subscription)) return null;
  return subscription.planId ?? null;
}

/**
 * Returns a human-readable string describing how much time remains until the
 * downgrade takes effect, e.g. "14 days", "3 hours", "less than an hour".
 * Returns null if no downgrade is pending.
 *
 * @param {object|null} subscription
 * @returns {string|null}
 */
export function getTimeUntilDowngrade(subscription) {
  if (!isDowngradePending(subscription)) return null;

  const msRemaining =
    new Date(subscription.currentPeriodEnd).getTime() - Date.now();

  const hours = Math.floor(msRemaining / (1000 * 60 * 60));
  if (hours < 1) return "less than an hour";

  const days = Math.floor(hours / 24);
  if (days < 1) return `${hours} hour${hours !== 1 ? "s" : ""}`;

  return `${days} day${days !== 1 ? "s" : ""}`;
}
